<script lang="ts" setup>
import type { AccountCreateOrUpdateOptions } from './Form.vue'
import type { AccountOpener } from '~/composables/useOpeners'
import type { AccountRole, RouterOutput, UpdateAccount, UpdateOrCreateAccount } from '~/types'
import { accountRoles } from '~/authorization'
import { allowedRolesToSet } from '~/authorization/permissions'

const props = defineProps<{
  opener: MaybeRef<AccountOpener>
  options?: Partial<AccountCreateOrUpdateOptions>
  creationRoles?: AccountRole[]
}>()
const emit = defineEmits<{ (e: 'submit', entity: RouterOutput['accounts']['createAccount']): void, (e: 'close'): void }>()

export type CustomSaveBtn = 'register.asUser' | 'register.asInstitution' | undefined

const formRef = useTemplateRef('formRef')
const {
  showUnsavedChangesModal,
  attemptClose,
  forceClose,
  cancelDiscard,
} = usePopupGuard(
  computed(() => formRef.value?.isDirty ?? false),
  () => emit('close'),
)
defineExpose({ attemptClose })

const opener = unref(props.opener)
const options = opener.mode === 'update'
  ? {
      ...props.options,
      showAccountDetails: opener.data.showAccountDetails ?? props.options?.showAccountDetails,
      showPersonDetails: opener.data.showPersonDetails ?? props.options?.showPersonDetails,
      showNameDetails: opener.data.showNameDetails ?? props.options?.showNameDetails,
    }
  : props.options

const { role } = useAuthorization()
const roles = computed(() => {
  const allowedRoles = allowedRolesToSet[role.value]
  const passedRoles = (opener.mode === 'create' && props.creationRoles) ? props.creationRoles : accountRoles.options.filter(role => role !== 'institution')

  return passedRoles.filter(role => !!allowedRoles && allowedRoles.includes(role))
})

const where = computed(() => useWhere('account', {
  id: opener.mode === 'update' ? opener.data.id : undefined,
}))

const { accountQuery, predispositionQuery } = useQuery()
const { data: account } = accountQuery.findOne(where)
const { data: predispositionsData } = predispositionQuery.findMany()
const predispositions = computed(() => predispositionsData.value?.data)

const payload = computed((): UpdateOrCreateAccount | undefined => {
  if (opener.mode === 'create') {
    return {
      mode: 'create',
      data: {
        email: '',
        role: roles.value[0] ?? 'person',
        person: {
          firstName: '',
          lastName: '',
          gender: 'none',
          isPregnant: 'none',
          isSmoker: 'none',
        },
      },
    }
  }

  if (account.value) {
    return {
      mode: 'update',
      data: {
        ...account.value,
        email: account.value.email ?? '',
        role: account.value.role as AccountRole,
        person: {
          ...account.value.person,
          relevantPredispositions: account.value.person?.relevantPredispositions?.map(({ id }) => id),
        },
      },
    } as UpdateAccount
  }
})

const isLoading = computed(() => create.isPending.value || update.isPending.value)

const { $i18n } = useNuxtApp()
const { $trpc, queryClient, useMutation, makeTrpcErrorToast, makeTrpcSuccessToast } = useMutationHelpers()
const create = useMutation({
  mutationFn: $trpc.accounts.createAccount.mutate,
  onError: makeTrpcErrorToast({ entity: $i18n.t('account.entity'), mode: 'create' }),
  onSuccess: async (account) => {
    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getAll'] })
    makeTrpcSuccessToast({ entity: $i18n.t('account.entity'), mode: 'create' })
    emit('submit', account)
    forceClose()
  },
})

const update = useMutation({
  mutationFn: $trpc.accounts.updateAccount.mutate,
  onError: makeTrpcErrorToast({ entity: $i18n.t('account.entity'), mode: 'update' }),

  onSuccess: async (account) => {
    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getAll'] })
    await queryClient.invalidateQueries({ queryKey: ['accounts', 'getById', { id: account.id }] })
    await queryClient.invalidateQueries({ queryKey: ['persons', 'getById', { id: account.personId }] })
    await Promise.all(account.legalRepresentativeOf.map(async ({ id }) => {
      return await queryClient.invalidateQueries({ queryKey: ['persons', 'getById', { id }] })
    }))
    makeTrpcSuccessToast({ entity: $i18n.t('account.entity'), mode: 'update' })
    emit('submit', account)
    forceClose()
  },
})

const saveCreateOrEditForm = (payload: UpdateOrCreateAccount) => {
  if (payload.data.email === '') {
    payload.data.email = null
  }
  if (payload.data.mobilePhone === '') {
    payload.data.mobilePhone = null
  }
  if (payload.mode === 'create') {
    create.mutate(payload.data)
  } else {
    update.mutate(payload.data)
  }
}
</script>

<template>
  <AccountCreateOrUpdateForm
    v-if="payload"
    ref="formRef"
    :payload="payload"
    :predispositions="predispositions"
    :roles="roles"
    :options="options"
    :is-loading="isLoading"
    @save="saveCreateOrEditForm"
    @cancel="attemptClose"
  />

  <TheConfirmPopup
    :show="showUnsavedChangesModal"
    :title="$t('popup.guard.title')"
    :text="$t('popup.guard.message')"
    :confirm-text="$t('popup.guard.discard')"
    :cancel-text="$t('popup.guard.cancel')"
    @close="cancelDiscard"
    @confirm="forceClose"
  />
</template>
